import axios from 'axios';
import { Client, ReconnectionTimeMode } from '@stomp/stompjs';
import { jwtDecode } from 'jwt-decode';
import { useDispatch } from 'react-redux';
import { logout, refreshAccessTokenWithStatus } from '../services/auth';
import {
  buildAuthHeaders,
  getAccessToken,
} from '../services/authTokenStore';
import { registerUserScopedClientStateCleaner } from '../services/clientSessionLifecycle';
import { resolveGatewayBackedServiceBaseUrl } from '../services/serviceBaseUrl';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const WS_URL = process.env.REACT_APP_WS_URL || 'wss://muzone.co/ws';
const API_BASE_URL = resolveGatewayBackedServiceBaseUrl(process.env.REACT_APP_API_URL);
const ACCESS_TOKEN_REFRESH_SKEW_MS = 60_000;

const StompContext = createContext({ client: null, isConnected: false });

export function StompProvider({ enabled = true, children }) {
  const dispatch = useDispatch();
  const clientRef = useRef(null);
  const authExpiredRef = useRef(false);
  const [client, setClient] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => registerUserScopedClientStateCleaner(() => {
    const activeClient = clientRef.current;
    clientRef.current = null;
    authExpiredRef.current = false;
    setClient(null);
    setIsConnected(false);
    return activeClient?.deactivate?.();
  }), []);

  const isAccessTokenExpiringSoon = useCallback((token) => {
    if (!token) {
      return true;
    }
    try {
      const decoded = jwtDecode(token);
      const expValue = decoded?.exp;
      if (typeof expValue !== 'number' || Number.isNaN(expValue)) {
        return true;
      }
      const expiresAtMs = expValue * 1000;
      return expiresAtMs - Date.now() <= ACCESS_TOKEN_REFRESH_SKEW_MS;
    } catch (_) {
      return true;
    }
  }, []);

  const refreshSessionForReconnect = useCallback(async () => {
    return refreshAccessTokenWithStatus();
  }, []);

  const hasActiveCookieSession = useCallback(async () => {
    try {
      await axios.get(`${API_BASE_URL}/api/auth/me`, { withCredentials: true, skipAuthRefresh: true });
      return true;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        return false;
      }
      // Treat transient failures as recoverable and keep reconnect logic alive.
      return true;
    }
  }, []);

  const ensureSessionForConnect = useCallback(async () => {
    const accessToken = getAccessToken();
    if (!isAccessTokenExpiringSoon(accessToken)) {
      return { ok: true, authExpired: false };
    }
    const refreshResult = await refreshSessionForReconnect();
    if (refreshResult.ok || !refreshResult.authExpired) {
      return { ok: true, authExpired: false };
    }
    const stillAuthenticated = await hasActiveCookieSession();
    return { ok: stillAuthenticated, authExpired: !stillAuthenticated };
  }, [hasActiveCookieSession, isAccessTokenExpiringSoon, refreshSessionForReconnect]);

  useEffect(() => {
    if (!enabled) {
      if (clientRef.current) {
        clientRef.current.deactivate();
        clientRef.current = null;
      }
      authExpiredRef.current = false;
      setClient(null);
      setIsConnected(false);
      return;
    }

    if (clientRef.current) {
      return;
    }

    let active = true;
    authExpiredRef.current = false;
    const stompClient = new Client({
      brokerURL: WS_URL,
      reconnectDelay: 1000,
      reconnectTimeMode: ReconnectionTimeMode.EXPONENTIAL,
      maxReconnectDelay: 30000,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      debug: () => {},
    });

    stompClient.beforeConnect = async () => {
      if (!active || authExpiredRef.current) {
        stompClient.reconnectDelay = 0;
        return;
      }
      const refreshResult = await ensureSessionForConnect();
      if (!refreshResult.ok && refreshResult.authExpired) {
        authExpiredRef.current = true;
        stompClient.reconnectDelay = 0;
        if (active) {
          await dispatch(logout());
        }
        throw new Error('WebSocket authentication expired');
      }
      stompClient.connectHeaders = buildAuthHeaders();
    };

    stompClient.onConnect = () => {
      if (active) setIsConnected(true);
    };
    stompClient.onDisconnect = () => {
      if (active) setIsConnected(false);
    };
    stompClient.onStompError = () => {
      if (active) setIsConnected(false);
    };
    stompClient.onWebSocketClose = () => {
      if (active) setIsConnected(false);
    };

    clientRef.current = stompClient;
    setClient(stompClient);
    stompClient.activate();

    return () => {
      active = false;
      authExpiredRef.current = false;
      stompClient.deactivate();
      if (clientRef.current === stompClient) {
        clientRef.current = null;
      }
      setClient(null);
      setIsConnected(false);
    };
  }, [dispatch, enabled, ensureSessionForConnect]);

  const value = useMemo(() => ({ client, isConnected }), [client, isConnected]);

  return <StompContext.Provider value={value}>{children}</StompContext.Provider>;
}

export function useStompClient() {
  return useContext(StompContext);
}
